<template>
  <GameDetailsSkeleton v-if="isLoading" />
  <div v-else-if="gameDetails" class="game-details">
    <GameInfo :gameDetails="gameDetails" />
    <br>
    <PredictionPreviews :gameDetails="gameDetails" :sport="sport" />
    <br>
    <TeamStatistics :gameDetails="gameDetails" :sport="sport" /> <!-- New component here -->
    <br>
    <GamePredictions :gameDetails="gameDetails" :sport="sport" />
    <br>
    <EditorComments :gameId="selectedGameId" :comments="comments" /> <!-- Pass comments as prop -->
  </div>
  <div v-else class="no-games">
    <div v-if="sport === 'Football'" class="empty-state-card">
      <img src="@/assets/balleralgo_logo.png" alt="BallerAlgo logo" class="empty-state-logo" />
      <h2>Football predictions, fixtures, and AI match analysis</h2>
      <p>
        Browse upcoming football matches, review AI-generated prediction categories, and move into league pages with
        featured fixtures and current standings.
      </p>
      <div class="empty-state-links">
        <router-link to="/football/league/english-premier-league">Premier League</router-link>
        <router-link to="/football/league/italian-serie-a">Serie A</router-link>
        <router-link to="/football/league/spanish-la-liga">La Liga</router-link>
        <router-link to="/leagues-we-cover">All Covered Leagues</router-link>
      </div>
    </div>
    <div v-else class="empty-state-card">
      <img src="@/assets/balleralgo_logo.png" alt="BallerAlgo logo" class="empty-state-logo" />
    </div>
  </div>
</template>


<script>
import GameInfo from './GameInfo.vue';
import GamePredictions from './GamePredictions.vue';
import EditorComments from './EditorComments.vue';
import GameDetailsSkeleton from './GameDetailsSkeleton.vue';
import PredictionPreviews from './PredictionPreviews.vue';
import TeamStatistics from './TeamStatistics.vue';
import { fetchJsonWithCache } from '../services/apiCache';
import { formatLeagueName } from '../utils/formatLeagueName';

const SITE_URL = 'https://www.balleralgo.com';

export default {
  components: {
    GameInfo,
    GamePredictions,
    EditorComments,
    GameDetailsSkeleton,
    PredictionPreviews,
    TeamStatistics
  },
  props: {
    selectedGameId: {
      type: [String, Number],
      default: null
    },
    sport: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      gameDetails: null,
      isLoading: false,
      comments: []  // Added to hold comments for the selected game
    };
  },
  watch: {
    selectedGameId: {
      immediate: true,
      handler(newId) {
        if (newId) {
          this.isLoading = true;
          this.gameDetails = null;
          this.comments = [];
          this.fetchGameDetails(newId);
          this.fetchComments(newId);  // Fetch comments when game ID changes
        } else {
          this.gameDetails = null;
          this.isLoading = false;
          this.comments = [];  // Clear comments when no game is selected
        }
      }
    }
  },
  methods: {
    async fetchGameDetails(gameId) {
      try {
        const apiUrl = `${process.env.VUE_APP_API_URL}/${this.sport}/fetchGameDetails/${gameId}`;
        this.gameDetails = await fetchJsonWithCache({
          key: `${this.sport}:game-details:${gameId}`,
          url: apiUrl,
          ttlMs: 15000,
        });
        this.applyMatchSeo(gameId);
      } catch (error) {
        console.error(`Failed to fetch game details for ${this.sport}:`, error);
        this.gameDetails = null;
        this.applyMissingMatchNoindex(gameId);
      } finally {
        this.isLoading = false;
      }
    },
    async fetchComments(gameId) {
      try {
        const apiUrl = `${process.env.VUE_APP_API_URL}/fetchGameComment/${gameId}`;
        this.comments = await fetchJsonWithCache({
          key: `comments:game:${gameId}`,
          url: apiUrl,
          ttlMs: 15000,
        });
      } catch (error) {
        console.error('Error fetching comments:', error.message);
        this.comments = [];  // Clear comments on error
      }
    },
    applyMatchSeo(gameId) {
      if (!this.gameDetails || this.sport !== 'Football') {
        return;
      }

      const firstTeam = this.gameDetails.first_team;
      const secondTeam = this.gameDetails.second_team;
      const leagueName = formatLeagueName(this.gameDetails.league);
      const title = `${firstTeam} vs ${secondTeam} Prediction, Odds & Match Analysis | BallerAlgo`;
      const description = `Review the AI prediction for ${firstTeam} vs ${secondTeam} in ${leagueName}. Compare top football markets, projected odds, confidence, and the match analysis on BallerAlgo.`;
      const canonicalUrl = `${SITE_URL}/football/${gameId}`;

      document.title = title;

      Array.from(document.querySelectorAll('[data-vue-router-controlled]')).forEach((el) => el.parentNode.removeChild(el));

      const headEntries = [
        { tag: 'link', attrs: { rel: 'canonical', href: canonicalUrl } },
        { tag: 'meta', attrs: { name: 'description', content: description } },
        { tag: 'meta', attrs: { property: 'og:title', content: title } },
        { tag: 'meta', attrs: { property: 'og:description', content: description } },
        { tag: 'meta', attrs: { property: 'og:type', content: 'article' } },
        { tag: 'meta', attrs: { property: 'og:url', content: canonicalUrl } },
        { tag: 'meta', attrs: { property: 'og:site_name', content: 'BallerAlgo' } },
        { tag: 'meta', attrs: { property: 'og:locale', content: 'en_US' } },
        { tag: 'meta', attrs: { name: 'twitter:card', content: 'summary_large_image' } },
        { tag: 'meta', attrs: { name: 'twitter:title', content: title } },
        { tag: 'meta', attrs: { name: 'twitter:description', content: description } },
        { tag: 'meta', attrs: { name: 'robots', content: 'index, follow' } },
      ];

      headEntries.forEach(({ tag, attrs }) => {
        const element = document.createElement(tag);
        Object.entries(attrs).forEach(([key, value]) => {
          element.setAttribute(key, value);
        });
        element.setAttribute('data-vue-router-controlled', '');
        document.head.appendChild(element);
      });

      const structuredData = [
        {
          '@context': 'https://schema.org',
          '@type': 'SportsEvent',
          name: `${firstTeam} vs ${secondTeam}`,
          description,
          startDate: this.gameDetails.date,
          url: canonicalUrl,
          sport: 'Football',
          competitor: [
            {
              '@type': 'SportsTeam',
              name: firstTeam,
            },
            {
              '@type': 'SportsTeam',
              name: secondTeam,
            },
          ],
          organizer: {
            '@type': 'Organization',
            name: 'BallerAlgo',
            url: SITE_URL,
          },
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: SITE_URL,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Football',
              item: `${SITE_URL}/football`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: leagueName,
              item: this.gameDetails.league_slug ? `${SITE_URL}/football/league/${this.gameDetails.league_slug}` : `${SITE_URL}/football`,
            },
            {
              '@type': 'ListItem',
              position: 4,
              name: `${firstTeam} vs ${secondTeam}`,
              item: canonicalUrl,
            },
          ],
        },
      ];

      structuredData.forEach((payload) => {
        const script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.setAttribute('data-vue-router-controlled', '');
        script.textContent = JSON.stringify(payload);
        document.head.appendChild(script);
      });
    },
    applyMissingMatchNoindex(gameId) {
      if (!gameId || this.sport !== 'Football') {
        return;
      }

      const canonicalUrl = `${SITE_URL}/football/${gameId}`;
      const title = 'Match Page Not Available | BallerAlgo';
      const description = 'This football match page does not currently have enough detail to be indexed on BallerAlgo.';

      document.title = title;
      Array.from(document.querySelectorAll('[data-vue-router-controlled]')).forEach((el) => el.parentNode.removeChild(el));

      const headEntries = [
        { tag: 'link', attrs: { rel: 'canonical', href: canonicalUrl } },
        { tag: 'meta', attrs: { name: 'description', content: description } },
        { tag: 'meta', attrs: { name: 'robots', content: 'noindex, follow' } },
      ];

      headEntries.forEach(({ tag, attrs }) => {
        const element = document.createElement(tag);
        Object.entries(attrs).forEach(([key, value]) => {
          element.setAttribute(key, value);
        });
        element.setAttribute('data-vue-router-controlled', '');
        document.head.appendChild(element);
      });
    }
  }
};
</script>

<style scoped>
.game-details {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 20px;
  background-color: #ffffff;
  min-width: 0;
}

.no-games {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 90%; /* Take full height of the viewport */
  padding: 32px 16px;
  text-align: center;
}

.empty-state-card {
  width: min(980px, 100%);
  padding: 28px 24px;
  border: 1px solid #e2eaf2;
  border-radius: 24px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  box-shadow: 0 18px 40px rgba(21, 41, 68, 0.06);
}

.empty-state-logo {
  width: min(260px, 70%);
  height: auto;
}

.empty-state-card h2 {
  margin: 18px 0 0;
  color: #173252;
  font-size: clamp(1.35rem, 2.2vw, 2rem);
  line-height: 1.2;
}

.empty-state-card p {
  max-width: 760px;
  margin: 12px auto 0;
  color: #5d7083;
  line-height: 1.7;
}

.empty-state-links {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-top: 18px;
}

.empty-state-links a {
  padding: 10px 16px;
  border: 1px solid #d7e3ef;
  border-radius: 999px;
  background: #fff;
  color: #173252;
  font-weight: 700;
  text-decoration: none;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.empty-state-links a:hover {
  background: #eef5fb;
  border-color: #bfd1e1;
}

@media (max-width: 900px) {
  .game-details {
    padding: 16px 12px 24px;
  }

  .no-games {
    height: auto;
    min-height: 260px;
  }

  .empty-state-card {
    padding: 22px 18px;
    border-radius: 20px;
  }
}

</style>
